import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowForward as ArrowIcon } from "@mui/icons-material";
import taskerService, {
  type TaskerTask,
} from "../../../services/taskerService";
import { readErrorMessage } from "../utils/mapBeErrors";
import { type OnTransitionSubmitted } from "./ReviewerRouter";
import { ReviewerPickers } from "./ReviewerPickers";
import { useReviewerTaskType } from "./reviewerQueries";
import { transitionForms } from "./taskConfig";
import { useRemarksMaxLength } from "./useRemarksMaxLength";
import { CharCounter } from "../components/CharCounter";
import { clampText } from "../utils/textLimits";
import { type BouncerRoleUser } from "../../../services/bouncerUsers";
import {
  FIELD_LABELS,
  isDocumentField,
  requiredReviewStages,
  stringifyValue,
  type ReviewStage,
} from "../../VendorProfile/edit/changeRequestFormat";
import {
  DocumentDiffCell,
  parseDocumentValue,
} from "../../VendorProfile/edit/DocumentDiffCell";

type Props = {
  task: TaskerTask;
  onSubmitted: OnTransitionSubmitted;
};

interface VdcrRequestData {
  change_request_code?: string;
  change_request_id?: string;
  section?: string;
  changed_fields?: string;
  old_values?: string;
  new_values?: string;
  vendor_legal_name?: string;
  facility_display_name?: string;
  requested_by?: string;
}

function safeParseObject(raw: unknown): Record<string, unknown> | null {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function labelFor(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

type Stage = ReviewStage;

interface StageConfig {
  approveAction: string;
  rejectAction: string;
  remarksLabel: string;
  needsReviewerSelectors: boolean;
}

const STAGE_CONFIG: Record<Stage, StageConfig> = {
  VM_REVIEW: {
    approveAction: "vm_approve",
    rejectAction: "vm_reject",
    remarksLabel: "Vendor Manager remarks",
    needsReviewerSelectors: true,
  },
  CM_REVIEW: {
    approveAction: "cm_approve",
    rejectAction: "cm_reject",
    remarksLabel: "Category Manager remarks",
    needsReviewerSelectors: false,
  },
  FINANCE_REVIEW: {
    approveAction: "finance_approve",
    rejectAction: "finance_reject",
    remarksLabel: "Business Finance remarks",
    needsReviewerSelectors: false,
  },
  COMPLIANCE_REVIEW: {
    approveAction: "compliance_approve",
    rejectAction: "compliance_reject",
    remarksLabel: "Compliance remarks",
    needsReviewerSelectors: false,
  },
};

const EDIT_REVIEW_FORM_KEYS: Record<Stage, string> = {
  VM_REVIEW: "vm_review",
  CM_REVIEW: "review_decision",
  FINANCE_REVIEW: "review_decision",
  COMPLIANCE_REVIEW: "review_decision",
};

const CANONICAL_ORDER: Stage[] = [
  "VM_REVIEW",
  "CM_REVIEW",
  "FINANCE_REVIEW",
  "COMPLIANCE_REVIEW",
];

const NEXT_STAGE_ROLE_LABEL: Record<Stage, string> = {
  VM_REVIEW: "Vendor Manager",
  CM_REVIEW: "Category Manager",
  FINANCE_REVIEW: "Business Finance",
  COMPLIANCE_REVIEW: "Compliance",
};

// BE auto-advances stages not in the chain; toast copy must skip them too.
function nextRequiredStage(
  current: Stage,
  chain: Set<Stage>,
): Stage | null {
  const idx = CANONICAL_ORDER.indexOf(current);
  for (let i = idx + 1; i < CANONICAL_ORDER.length; i += 1) {
    if (chain.has(CANONICAL_ORDER[i])) return CANONICAL_ORDER[i];
  }
  return null;
}

function stageOf(task: TaskerTask): Stage | null {
  const status = task.current_status;
  if (
    status === "VM_REVIEW" ||
    status === "CM_REVIEW" ||
    status === "FINANCE_REVIEW" ||
    status === "COMPLIANCE_REVIEW"
  ) {
    return status;
  }
  const terminal =
    status === "APPROVED" || status === "REJECTED" || status === "REVISE";
  if (!terminal && process.env.NODE_ENV !== "production") {
    console.warn(
      `[VDCR] Unrecognised task.current_status "${status}" — treating as terminal.`,
    );
  }
  return null;
}

export function VendorDetailChangeReviewPanel({ task, onSubmitted }: Props) {
  const data = (task.request_data ?? {}) as VdcrRequestData;
  const oldValues = safeParseObject(data.old_values) ?? {};
  const newValues = safeParseObject(data.new_values) ?? {};
  const rawFields = data.changed_fields as unknown;
  const fields = (
    Array.isArray(rawFields) ? rawFields : (rawFields ?? "").toString().split(",")
  )
    .map((f: unknown) => String(f).trim())
    .filter(Boolean);

  const stage = stageOf(task);
  const isTerminal = !stage;
  const terminalStatus = isTerminal ? task.current_status : "";
  const stageCfg: StageConfig = stage
    ? STAGE_CONFIG[stage]
    : STAGE_CONFIG.VM_REVIEW;
  const taskTypeQuery = useReviewerTaskType(task.task_type_id);
  const remarksFormId = stage
    ? transitionForms(taskTypeQuery.data?.task_config)?.[EDIT_REVIEW_FORM_KEYS[stage]]
    : undefined;
  const remarksMaxLength = useRemarksMaxLength(remarksFormId, "remarks");

  const chainStages = useMemo(() => requiredReviewStages(fields), [fields]);
  const showCmPicker =
    !isTerminal &&
    stageCfg.needsReviewerSelectors &&
    chainStages.has("CM_REVIEW");
  const showFmPicker =
    !isTerminal &&
    stageCfg.needsReviewerSelectors &&
    chainStages.has("FINANCE_REVIEW");
  const nextStage = stage ? nextRequiredStage(stage, chainStages) : null;
  const approveButtonLabel = nextStage ? "Approve" : "Approve & apply";
  const approvedToastSuffix = nextStage
    ? `approved. Sent to ${NEXT_STAGE_ROLE_LABEL[nextStage]} for the next review.`
    : "approved and applied.";

  const [remarks, setRemarks] = useState("");
  const [submittingKind, setSubmittingKind] = useState<
    "approve" | "reject" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [cm, setCm] = useState<BouncerRoleUser[]>([]);
  const [bizFin, setBizFin] = useState<BouncerRoleUser | null>(null);
  const submitting = submittingKind !== null;

  useEffect(() => {
    setRemarks("");
    setSubmittingKind(null);
    setError(null);
    setCm([]);
    setBizFin(null);
  }, [task.id]);


  const runTransition = async (kind: "approve" | "reject") => {
    if (!task.id) return;
    if (kind === "reject" && !remarks.trim()) {
      setError("A rejection reason is required.");
      return;
    }
    if (kind === "approve") {
      if (showCmPicker && !cm.length) {
        setError("Select at least one Category Manager reviewer before approving.");
        return;
      }
      if (showFmPicker && !bizFin) {
        setError("Select a Business Finance reviewer before approving.");
        return;
      }
    }
    setSubmittingKind(kind);
    setError(null);
    const action =
      kind === "approve" ? stageCfg.approveAction : stageCfg.rejectAction;
    const remarksField =
      kind === "reject" || remarks.trim()
        ? { remarks: remarks.trim() }
        : undefined;
    const reviewerFields =
      kind === "approve"
        ? {
            ...(showCmPicker && cm.length
              ? { category_manager_email: cm.map((u) => u.email_id) }
              : {}),
            ...(showFmPicker && bizFin
              ? { finance_manager_email: bizFin.email_id }
              : {}),
          }
        : undefined;
    const hasReviewerFields =
      reviewerFields && Object.keys(reviewerFields).length > 0;
    const formData =
      remarksField || hasReviewerFields
        ? { ...(remarksField ?? {}), ...(hasReviewerFields ? reviewerFields : {}) }
        : undefined;
    try {
      await taskerService.executeTransition(task.id, action, formData, "reviewer");
      const codePrefix = data.change_request_code ?? "Change request";
      if (kind === "approve") {
        onSubmitted("approved", `${codePrefix} ${approvedToastSuffix}`);
      } else {
        onSubmitted(
          "rejected",
          `${codePrefix} rejected and returned to the vendor.`,
        );
      }
    } catch (err) {
      setError(
        readErrorMessage(
          err,
          kind === "approve"
            ? "Couldn't submit approval. Try again."
            : "Couldn't submit rejection. Try again.",
        ),
      );
    } finally {
      setSubmittingKind(null);
    }
  };

  return (
    <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          border: 1,
          borderColor: "#E0E4EF",
          borderRadius: 1.5,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
            {data.change_request_code && (
              <Typography sx={{ fontFamily: "monospace", fontSize: 15, fontWeight: 700 }}>
                {data.change_request_code}
              </Typography>
            )}
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
              {fields.map(labelFor).join(", ")}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={stage ?? terminalStatus ?? "PENDING"}
            sx={{
              bgcolor:
                terminalStatus === "APPROVED"
                  ? "#E7F5EC"
                  : terminalStatus === "REJECTED"
                    ? "#FDECEA"
                    : "#FFF3D6",
              color:
                terminalStatus === "APPROVED"
                  ? "#0F5132"
                  : terminalStatus === "REJECTED"
                    ? "#7F1D1D"
                    : "#B45309",
              fontWeight: 700,
              letterSpacing: 0.4,
              height: 24,
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              color: "text.secondary",
              mb: 1,
            }}
          >
            REQUESTED CHANGE
          </Typography>
          <Stack spacing={1.25}>
            {fields.length === 0 && (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                No field-level diff on this task.
              </Typography>
            )}
            {fields.map((field) => {
              const isDoc = isDocumentField(field);
              return (
                <Box
                  key={field}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    fontSize: 13,
                  }}
                >
                  {isDoc ? (
                    <DocumentDiffCell
                      doc={parseDocumentValue(oldValues[field])}
                      strikethrough
                      onError={setError}
                    />
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        textDecoration: "line-through",
                        color: "text.secondary",
                      }}
                    >
                      {stringifyValue(oldValues[field])}
                    </Box>
                  )}
                  <ArrowIcon sx={{ fontSize: 16, color: "primary.main" }} />
                  {isDoc ? (
                    <DocumentDiffCell
                      doc={parseDocumentValue(newValues[field])}
                      onError={setError}
                    />
                  ) : (
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {stringifyValue(newValues[field])}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>

      {isTerminal && (
        <Alert
          severity={
            terminalStatus === "APPROVED"
              ? "success"
              : terminalStatus === "REJECTED"
                ? "error"
                : "warning"
          }
          sx={{ mt: 0 }}
        >
          {terminalStatus === "APPROVED"
            ? "This change request has been approved and applied. No further action required."
            : terminalStatus === "REJECTED"
              ? "This change request has been rejected and returned to the vendor."
              : `This change request is currently ${task.current_status} — no reviewer action available at this step.`}
        </Alert>
      )}

      {!isTerminal && (
      <>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
          {stageCfg.remarksLabel}{" "}
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
            (mandatory to reject)
          </Box>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder="Add your review note"
          value={remarks}
          onChange={(e) => {
            setRemarks(clampText(e.target.value, remarksMaxLength));
            if (error) setError(null);
          }}
          inputProps={{ "aria-label": stageCfg.remarksLabel, maxLength: remarksMaxLength }}
        />
        <CharCounter length={remarks.length} max={remarksMaxLength} />
      </Box>

      <ReviewerPickers
        showCm={showCmPicker}
        showFm={showFmPicker}
        cm={cm}
        bizFin={bizFin}
        onCmChange={(next) => {
          setCm(next);
          if (error) setError(null);
        }}
        onFmChange={(next) => {
          setBizFin(next);
          if (error) setError(null);
        }}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          disabled={submitting}
          onClick={() => runTransition("reject")}
          startIcon={
            submittingKind === "reject" ? <CircularProgress size={16} /> : undefined
          }
          sx={{ height: 44, textTransform: "none", fontWeight: 600 }}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={submitting}
          onClick={() => runTransition("approve")}
          startIcon={
            submittingKind === "approve" ? <CircularProgress size={16} /> : undefined
          }
          sx={{ height: 44, textTransform: "none", fontWeight: 600 }}
        >
          {approveButtonLabel}
        </Button>
      </Box>
      </>
      )}
    </Box>
  );
}

export default VendorDetailChangeReviewPanel;
